import { useGridUserDataGlobalFiltersContext } from 'contexts/GridUserDataGlobalFiltersProvider';
import { ScannerLogContextType } from 'entities/ScannerLogContextType';
import { useCallback, useEffect, useRef, useState } from 'react';
import { logScannerAction } from 'utils/scanner/logScannerAction';

export interface UseScannerInputProps {
  onScan: (code: string) => void;
  enabled: boolean;
  /**
   * Time threshold (in ms) to detect if input is from scanner (fast typing)
   * Default: 100ms
   */
  scannerThreshold?: number;
  /** Scan-log context type (ScannerLogContextType); defaults to misc. */
  contextType?: number;
}

export interface UseScannerInputResult {
  scannedCode: string;
  setScannedCode: (code: string) => void;
  handleManualSubmit: () => void;
}

/**
 * Hook for handling scanner input (both physical scanner and keyboard emulation).
 *
 * Detects fast keyboard input (typical of barcode scanners) and triggers
 * onScan callback when Enter is pressed.
 *
 * @example
 * const { scannedCode, setScannedCode, handleManualSubmit } = useScannerInput({
 *   onScan: (code) => console.log('Scanned:', code),
 *   enabled: true,
 * });
 */
export const useScannerInput = ({
  onScan,
  enabled,
  scannerThreshold = 100,
  contextType = ScannerLogContextType.other,
}: UseScannerInputProps): UseScannerInputResult => {
  const [scannedCode, setScannedCode] = useState('');
  const scannerBuffer = useRef('');
  const lastKeyTime = useRef(0);

  // Terminal for the scan log comes from the global terminal filter; ref keeps the keydown handler stable.
  const { data: globalFilters = {} } = useGridUserDataGlobalFiltersContext();
  const terminalIdRef = useRef<number | null>(null);
  terminalIdRef.current = globalFilters?.terminal_id?.[0] ?? null;

  const handleScan = useCallback((code: string) => {
    if (code.trim()) {
      onScan(code.trim());
    }
  }, [onScan]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;

    // Ignore input if user is typing in an input field
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      return;
    }

    const currentTime = Date.now();

    // Reset buffer if too much time has passed (not scanner input)
    if (currentTime - lastKeyTime.current > scannerThreshold) {
      scannerBuffer.current = '';
    }
    lastKeyTime.current = currentTime;

    if (e.key === 'Enter') {
      // Submit scanned code on Enter
      if (scannerBuffer.current.length > 0) {
        // keyboard-wedge scanners carry no device identity, so no scanner label
        logScannerAction(scannerBuffer.current, { contextType, terminalId: terminalIdRef.current });
        handleScan(scannerBuffer.current);
        setScannedCode(scannerBuffer.current);
        scannerBuffer.current = '';
      }
    } else if (e.key.length === 1) {
      // Accumulate single characters
      scannerBuffer.current += e.key;
    }
  }, [enabled, handleScan, scannerThreshold, contextType]);

  const handleManualSubmit = useCallback(() => {
    if (scannedCode.trim()) {
      handleScan(scannedCode);
    }
  }, [scannedCode, handleScan]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  return {
    scannedCode,
    setScannedCode,
    handleManualSubmit,
  };
};
